import type { Row, Column } from '@tanstack/react-table';

/**
 * Escape a CSV cell value — wraps in quotes if it contains commas, quotes, or newlines.
 */
function escapeCSV(value: unknown): string {
  if (value == null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Export selected rows to a CSV file and trigger a browser download.
 *
 * - Skips the __select (checkbox) column
 * - Uses column meta.label for header names
 * - Prepends UTF-8 BOM for Thai text Excel compatibility
 */
export function exportSelectedRowsToCSV<T>(
  rows: Row<T>[],
  columns: Column<T>[],
  filename: string,
): void {
  // Filter out non-data columns (checkbox, etc.)
  const dataColumns = columns.filter(
    (col) => col.id !== '__select' && col.getIsVisible(),
  );

  if (dataColumns.length === 0 || rows.length === 0) return;

  // Header row
  const headers = dataColumns.map(
    (col) => escapeCSV((col.columnDef.meta as { label?: string })?.label ?? col.id),
  );

  // Data rows
  const dataRows = rows.map((row) =>
    dataColumns.map((col) => escapeCSV(row.getValue(col.id))),
  );

  // Build CSV with BOM for Thai text Excel compatibility
  const BOM = '\uFEFF';
  const csv = BOM + [headers.join(','), ...dataRows.map((r) => r.join(','))].join('\n');

  // Trigger download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
